import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Shield, KeyRound, AlertCircle, Loader2, Info } from "lucide-react";
import { Modal } from "../ui";
import { ForgotCodeModal } from "./ForgotCodeModal";
import { ForgotPasswordModal } from "./ForgotPasswordModal";
import { AuthBrandLogo } from "./AuthBrandLogo";
import { useAuth } from "../../hooks/useAuth";
import type { User, UserRole } from "../../types";
import { getBranding } from "../../config/branding";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  role: UserRole | null;
  onSuccess: (user: User) => void;
}

const inputClass =
  "w-full px-4 py-3.5 rounded-xl border border-gray-200/80 dark:border-white/10 bg-gray-50/80 dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/40 outline-none transition-all duration-200 focus:border-gray-300 dark:focus:border-white/20 focus:ring-[3px] focus:ring-gray-200/50 dark:focus:ring-white/10 text-[15px]";

const btnPrimaryClass =
  "w-full py-3.5 rounded-xl font-medium text-[15px] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:opacity-90 active:scale-[0.99]";

export function LoginModal({ isOpen, onClose, role, onSuccess }: LoginModalProps) {
  const { t } = useTranslation();
  const branding = getBranding();
  const { loginAdmin, loginWithCode, getMagicLinkForCode, verifyTokenHashAndGetUser, registerAdmin, requestVerificationCode, verifyCodeAndRecover, verifyMfaAndGetUser } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [showForgotCode, setShowForgotCode] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [needsMfaStep, setNeedsMfaStep] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setPassword("");
      setAccessCode("");
      setMfaCode("");
      setError("");
      setIsRegistering(false);
      setShowForgotCode(false);
      setShowForgotPassword(false);
      setNeedsMfaStep(false);
      setLoading(false);
    }
  }, [isOpen]);

  const handleRequestVerification = async (email: string) => {
    if (!role || role === "admin") {
      return { success: false, error: t("auth.invalidRole") };
    }
    return await requestVerificationCode(role as "partner" | "installer" | "commercial", email);
  };

  const handleVerifyCode = async (email: string, verificationCode: string) => {
    if (!role || role === "admin") {
      return { code: null, name: null, error: t("auth.invalidRole") };
    }
    return await verifyCodeAndRecover(role as "partner" | "installer" | "commercial", email, verificationCode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (role === "admin") {
        if (isRegistering) {
          if (!name.trim() || !email.trim() || !password) {
            setError(t("auth.allFieldsRequired"));
            return;
          }
          const { user, error: err } = await registerAdmin(name.trim(), email.trim(), password);
          if (err) {
            setError(err);
            return;
          }
          if (user) {
            onSuccess(user);
            onClose();
          }
        } else {
          if (!email.trim() || !password) {
            setError(t("auth.emailPasswordRequired"));
            return;
          }
          const result = await loginAdmin(email.trim(), password);
          if (result.error) {
            const isInvalidCreds = /invalid login credentials/i.test(result.error);
            const isLocalhost = typeof window !== "undefined" && /localhost|127\.0\.0\.1/.test(window.location.hostname);
            if (isInvalidCreds) {
              setError(
                isLocalhost
                  ? `${t("auth.invalidLoginCredentials")} ${t("auth.invalidLoginCredentialsLocal")}`
                  : t("auth.invalidLoginCredentials")
              );
            } else {
              setError(result.error);
            }
            return;
          }
          if (result.needsMfa) {
            setNeedsMfaStep(true);
            return;
          }
          if (result.user) {
            onSuccess(result.user);
            onClose();
          }
        }
      } else if (role === "partner" || role === "installer" || role === "commercial") {
        if (!accessCode.trim()) {
          setError(t("auth.accessCodeRequired"));
          return;
        }
        // Connexion par code : priorité token_hash (session sans redirection), sinon URL magic link, sinon fallback ancien
        const { url, token_hash, profile: profileFromApi, error: err } = await getMagicLinkForCode(role, accessCode.trim());
        if (token_hash) {
          const { user, error: verifyErr } = await verifyTokenHashAndGetUser(token_hash, profileFromApi ?? undefined);
          if (verifyErr) {
            setError(verifyErr);
            return;
          }
          if (user) {
            onSuccess(user);
            onClose();
            return;
          }
        }
        if (url) {
          window.location.href = url;
          return;
        }
        const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
        const redirectUrl = typeof window !== "undefined" ? `${window.location.origin}/` : "";
        if (err && !err.includes("non configuré") && !err.includes("Contactez l")) {
          const msg = isLocal
            ? `${err} En local, ajoutez cette URL dans Supabase → Auth → URL Configuration → Redirect URLs : ${redirectUrl} Utilisez le code complet (ex. ABCD-1234).`
            : err;
          setError(msg);
          return;
        }
        // Fallback : partenaire/poseur créé avant (sans compte Auth) → connexion par code seule (souvent bloqué par RLS)
        const { user, error: codeErr } = await loginWithCode(role, accessCode.trim());
        if (codeErr) {
          setError(isLocal && err
            ? `${err} Redirect URL en local : ${redirectUrl}`
            : (err || codeErr));
          return;
        }
        if (user) {
          onSuccess(user);
          onClose();
        } else {
          setError(
            isLocal
              ? `${t("auth.invalidCode") || "Code invalide ou inactif."} En local : ajoutez ${redirectUrl} dans Supabase (Redirect URLs) et utilisez le code complet.`
              : (t("auth.invalidCode") || "Code invalide ou inactif.")
          );
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaCode.trim() || mfaCode.trim().length < 6) {
      setError(t("auth.mfaCodeRequired"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { user: mfaUser, error: mfaErr } = await verifyMfaAndGetUser(mfaCode.trim());
      if (mfaErr) {
        setError(mfaErr);
        return;
      }
      if (mfaUser) {
        onSuccess(mfaUser);
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  if (!role) return null;

  if (role === "admin" && needsMfaStep) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} variant="premium">
        <div className="pt-2 pb-1">
          <div className="flex flex-col items-center text-center mb-8">
            <div className="mb-4 flex flex-col items-center gap-2">
              <AuthBrandLogo branding={branding} size="modal" />
              {branding.logoDisplay !== "full" && (
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 tracking-wide">
                  {branding.brandName}
                </p>
              )}
            </div>
            <div className="w-14 h-14 rounded-full bg-gray-100/80 dark:bg-white/10 flex items-center justify-center mb-5 text-gray-600 dark:text-gray-300">
              <Shield size={26} strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
              {t("auth.mfaTitle")}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 max-w-[260px]">
              {t("auth.mfaDescription")}
            </p>
          </div>
          <form onSubmit={handleMfaSubmit} className="space-y-5">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
              className={`${inputClass} text-center text-lg tracking-[0.35em] font-medium`}
              placeholder="000000"
              autoFocus
            />
            {error && (
              <div className="flex items-center justify-center gap-2 text-red-500 dark:text-red-400 text-xs font-medium">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className={btnPrimaryClass}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("auth.mfaVerify")}
            </button>
            <button
              type="button"
              onClick={() => { setNeedsMfaStep(false); setMfaCode(""); setError(""); }}
              className="w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
            >
              {t("auth.mfaBack")}
            </button>
          </form>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} variant="premium">
      <div className="pt-2 pb-1">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="mb-4 flex flex-col items-center gap-2">
            <AuthBrandLogo branding={branding} size="modal" />
            {branding.logoDisplay !== "full" && (
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 tracking-wide">
                {branding.brandName}
              </p>
            )}
          </div>
          <div className="w-14 h-14 rounded-full bg-gray-100/80 dark:bg-white/10 flex items-center justify-center mb-5 text-gray-600 dark:text-gray-300">
            {role === "admin" ? <Shield size={26} strokeWidth={1.5} /> : <KeyRound size={26} strokeWidth={1.5} />}
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
            {isRegistering
              ? t("auth.createAccount")
              : role === "admin"
                ? t("auth.accessAdmin")
                : role === "partner"
                  ? t("auth.accessPartner")
                  : role === "installer"
                    ? t("auth.accessInstaller")
                    : t("auth.accessCommercial")}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
            {t("auth.secureAccess")}
          </p>
          {(role === "partner" || role === "installer" || role === "commercial") &&
            typeof window !== "undefined" &&
            (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") && (
            <div className="mt-4 w-full rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-left">
              <p className="text-xs font-medium text-amber-800 dark:text-amber-200 flex items-start gap-2">
                <Info size={14} className="shrink-0 mt-0.5" />
                <span>
                  Pour rester connecté en local (et ne pas être redirigé vers le site en ligne), ajoutez cette URL dans Supabase → Authentication → URL Configuration → <strong>Redirect URLs</strong> :
                </span>
              </p>
              <p className="mt-2 text-xs font-mono text-amber-900 dark:text-amber-100 bg-white/50 dark:bg-black/20 px-2 py-1.5 rounded break-all">
                {window.location.origin}/
              </p>
              <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">
                (avec le slash final)
              </p>
              <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                Sans cette configuration, Supabase vous renverra vers le site en production après le lien magique.
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {role === "admin" ? (
            <>
              {isRegistering && (
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                  placeholder={t("auth.namePlaceholder")}
                />
              )}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder={t("auth.emailPlaceholder")}
                autoComplete="email"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder={t("auth.passwordPlaceholder")}
                autoComplete={isRegistering ? "new-password" : "current-password"}
              />
            </>
          ) : (
            <input
              type="text"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              className={`${inputClass} text-center tracking-[0.2em] font-medium uppercase`}
              placeholder={t("auth.codePlaceholder")}
              autoFocus
            />
          )}

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-500 dark:text-red-400 text-xs font-medium">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className={btnPrimaryClass}>
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isRegistering ? (
              t("auth.register")
            ) : (
              t("auth.login")
            )}
          </button>

          {role === "admin" && (
            <div className="text-center pt-1 space-y-2">
              <button
                type="button"
                onClick={() => { setIsRegistering(!isRegistering); setError(""); }}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors block w-full"
              >
                {isRegistering ? t("auth.alreadyHaveAccount") : t("auth.createAdminAccount")}
              </button>
              {!isRegistering && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors block w-full"
                >
                  {t("auth.forgotPassword")}
                </button>
              )}
            </div>
          )}

          {(role === "partner" || role === "installer" || role === "commercial") && (
            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => setShowForgotCode(true)}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
              >
                {t("auth.forgotCode")}
              </button>
            </div>
          )}
        </form>
      </div>

      <ForgotCodeModal
        isOpen={showForgotCode}
        onClose={() => setShowForgotCode(false)}
        role={role}
        onRequestVerification={handleRequestVerification}
        onVerifyCode={handleVerifyCode}
      />

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </Modal>
  );
}
